/**
 * The inbox pipeline's building blocks (product spec section 7): the pure
 * merges — read+classify → remember → match → triage for receipts, and the
 * section 9 priority list for bank rows — plus the small database helpers
 * around them. `reconcile.ts` runs the merges over the whole company from the
 * stored facts; `run.ts` adds the AI steps and sequences one processing run.
 * AI, Drive and media are injected (`InboxDeps`); nothing here books.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { applyMerchantRule, categoryDisagreesWith, isAmbiguousCategory, memoryReasonText, mixedMemoryReasonText } from '../merchants/service';
import { merchantKeyOf } from '../merchants/normalize';
import { catalogFor } from '../catalog/catalog';
import { findCategory } from '../catalog/rules';
import type { Ore } from '../money';
import type { Category } from '../catalog/types';
import { getCompanySettings } from '../company-settings/service';
import { latestLockedThrough } from '../periods/service';
import {
  AUTO_LINK_MIN_GAP,
  decide,
  decideRows,
  findTransferPair,
  looksLikeOwnTransferText,
  rankBankRowsForReceipt,
  rankCandidates,
  rankCombinations,
  rankCreditNoteTargets,
  rankRefundCandidates,
  reconciledText,
} from '../bank/match';
import type { Candidate, RefundablePurchase, RowCandidate } from '../bank/match';
import { combinationQuestion, isSkatteverketText, matchQuestion, moneyInQuestion, receiptQuestion, refundQuestion, taxQuestion, transferQuestion } from '../bank/questions';
import type { QuestionCard } from '../bank/questions';
import { formatSek } from '../money';
import { formatSwedishDate } from '../periods/deadlines';
import type {
  Account,
  AiSettings,
  BankRow,
  Company,
  CompanySettings,
  DecidedField,
  FiscalYear,
  InboxItem,
  IsoDate,
  MerchantRule,
  PaymentSource,
  Proposal,
  ProposalAllocation,
  ProposalQuestion,
  ReceiptReading,
  TaxSubkind,
  TransactionKind,
  Uuid,
  Verifikation,
} from '../types';
import type { BankRowClassificationResult, InboxDeps, InboxProposal, PipelineUiError, ProcessedItem, ReceiptReadResult } from './internal-types';
import { NO_VAT_BREAKDOWN_QUESTION, TRIAGE_REASON, looksLikeDuplicate, triage } from './triage';
import type { OpenItem, TriageInput } from './types';

// ---- Copy ---------------------------------------------------------------------

export const WAITING_FOR_AI = 'Kvittona väntar på AI. Anslut AI eller fyll i själv.';
export const WAITING_FOR_QUOTA = 'Väntar på AI\u2011kvot (gratisnivån har en gräns per minut)';
export const WAITING_FOR_DAY_QUOTA = 'Dagskvoten hos AI-tjänsten är slut – läsningen fortsätter i morgon eller när du aktiverar betald nivå.';
export const FILE_MISSING = 'Filen saknas lokalt – ladda upp kvittot igen.';
export const BANK_ROW_MISSING = 'Bankraden finns inte längre.';
export const GENERIC_AI_ERROR = 'AI-tjänsten svarade inte som väntat. Försök igen eller fyll i själv.';
export const MISSING_DOCUMENT_LINKED = (v: { series: string; number: number }) => `Kvitto till ett köp som bokfördes utan kvitto (${v.series}${v.number}).`;
/** A bank row that pays the owner back for a purchase made with their own money. */
export const REIMBURSEMENT_REASON = (label: string, merchant: string) => `Återbetalning av ditt utlägg ${label}${merchant ? ` (${merchant})` : ''}`;
export const INVOICE_SETTLEMENT_REASON = (label: string, counterparty: string) => `Betalning av faktura ${label} (${counterparty || 'okänd'})`;
export const PARTIAL_SETTLEMENT_REASON = (label: string, counterparty: string, remainingOre: Ore) =>
  `Delbetalning av faktura ${label} (${counterparty || 'okänd'}) – ${formatSek(remainingOre)} kr återstår efter den här`;
export const COMBINED_SETTLEMENT_REASON = (labels: string[], counterparty: string) => `Betalning av fakturorna ${labels.join(', ')} (${counterparty || 'okänd'})`;
export const REFUND_REASON = (label: string, merchant: string) => `Återbetalning av köpet ${label}${merchant ? ` (${merchant})` : ''}`;
export const CREDIT_NOTE_REASON = (label: string, merchant: string) => `Kreditnota som minskar köpet ${label}${merchant ? ` (${merchant})` : ''}`;
export const CREDIT_NOTE_TARGET_MISSING = 'Hittar inget köp eller någon faktura som kreditnotan hör till – välj det under Ändra.';
/** The receipt has several bank rows that could have paid it; the user picks. */
export const AMBIGUOUS_ROWS_REASON = 'Flera banktransaktioner kan ha betalat köpet – välj vilken.';
export const MATCH_QUESTION_TEXT = 'Vilken banktransaktion betalade köpet?';
export const MATCH_NONE_LABEL = 'Ingen av dem';
/** Answer value on the receipt's match question that rejects every listed row. */
export const MATCH_NONE_VALUE = 'none';

export const DEFAULT_MAX_RATE_LIMIT_RETRIES = 3;
export const MAX_BACKOFF_SEC = 120;
/** Rows the AI classifies per call. */
export const BANK_CLASSIFY_BATCH = 40;
/** Few-shot history sent with a bank classification. */
export const BANK_HISTORY_LIMIT = 30;
/**
 * An item left in `processing` this long was abandoned by a run that never
 * finished (a closed tab, a crash). The next run takes it back; its stored
 * extraction, when it has one, spares the second AI call.
 */
export const STALE_PROCESSING_MS = 10 * 60_000;

// ---- Company context ------------------------------------------------------------

export interface CompanyContext {
  company: Company;
  settings: CompanySettings;
  fiscalYears: FiscalYear[];
  openYears: FiscalYear[];
  accounts: Account[];
  accountMap: Map<number, Account>;
  activeAccounts: Set<number>;
  paymentSources: PaymentSource[];
  sourceMap: Map<Uuid, PaymentSource>;
  catalog: Category[];
  lockedThrough: IsoDate | undefined;
}

export async function loadCompanyContext(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<CompanyContext> {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);
  const [settings, fiscalYears, accounts, paymentSources, locks] = await Promise.all([
    getCompanySettings(companyId, db),
    db.fiscalYears.where('companyId').equals(companyId).sortBy('startDate'),
    db.accounts.where('companyId').equals(companyId).toArray(),
    db.paymentSources.where('companyId').equals(companyId).toArray(),
    db.periodLocks.where('companyId').equals(companyId).toArray(),
  ]);
  return {
    company,
    settings,
    fiscalYears,
    openYears: fiscalYears.filter((fy) => fy.status === 'open'),
    accounts,
    accountMap: new Map(accounts.map((a) => [a.number, a])),
    activeAccounts: new Set(accounts.filter((a) => a.active).map((a) => a.number)),
    paymentSources,
    sourceMap: new Map(paymentSources.map((s) => [s.id, s])),
    catalog: catalogFor(company.entityType, { arsavgiftAccount: settings.arsavgiftAccount }),
    lockedThrough: latestLockedThrough(locks),
  };
}

// ---- Small pure helpers ----------------------------------------------------------

/** Keys with `undefined` removed, so Dexie updates never store `undefined`. */
export function compact<T extends object>(obj: T): { [K in keyof T]?: Exclude<T[K], undefined> } {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out as { [K in keyof T]?: Exclude<T[K], undefined> };
}

/** The Skatteverket answer stored on a proposal (see `InboxProposal`). */
export function taxSubkindOf(proposal: Proposal | undefined): TaxSubkind | undefined {
  return (proposal as InboxProposal | undefined)?.taxSubkind ?? undefined;
}

export function todayIso(): IsoDate {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The provider's own wording carried on an `AiError` ("HTTP 404 · NOT_FOUND ·
 * …"). Read structurally so the domain keeps its one-way dependency on `ai`;
 * `ai/errors` scrubs anything key-shaped before it gets here.
 */
export function technicalOfError(err: unknown): string | undefined {
  const ui = (err as { ui?: { technical?: unknown } } | null | undefined)?.ui;
  return typeof ui?.technical === 'string' && ui.technical ? ui.technical : undefined;
}

/** Local check mirroring `ai/provider.isAiConfigured` without importing it. */
export function aiConfigured(ai: AiSettings): boolean {
  switch (ai.provider) {
    case 'gemini':
    case 'openai':
      return ai.apiKey.trim() !== '' && ai.model.trim() !== '';
    case 'openai-compatible':
      return ai.baseUrl.trim() !== '' && ai.model.trim() !== '';
    default: {
      const exhaustive: never = ai.provider;
      throw new Error(`Unknown provider ${String(exhaustive)}`);
    }
  }
}

/**
 * Payment source for a receipt's payment hint: card/swish sources when the
 * company has one (else the default bank source), cash for cash, otherwise
 * the company default.
 */
export function paymentSourceForHint(hint: ReceiptReading['paymentHint'], sources: readonly PaymentSource[], settings: CompanySettings): Uuid | null {
  const active = sources.filter((s) => s.active);
  const byKind = (kind: PaymentSource['kind']) => active.find((s) => s.kind === kind)?.id ?? null;
  const fallback = settings.defaultPaymentSourceId ?? byKind('bank');
  switch (hint) {
    case 'card':
      return byKind('card') ?? fallback;
    case 'swish':
      return byKind('swish') ?? fallback;
    case 'cash':
      return byKind('cash') ?? fallback;
    case 'invoice':
    case 'unknown':
      return fallback;
    default: {
      const exhaustive: never = hint;
      throw new Error(`Unknown payment hint ${String(exhaustive)}`);
    }
  }
}

/** Bank sources a receipt paid from `sourceId` could show up on (card/swish → their bank source). */
export function bankSourcesFor(sourceId: Uuid | null, sourceMap: ReadonlyMap<Uuid, PaymentSource>): Set<Uuid> {
  const out = new Set<Uuid>();
  if (!sourceId) return out;
  const s = sourceMap.get(sourceId);
  if (!s) return out;
  switch (s.kind) {
    case 'bank':
      out.add(s.id);
      break;
    case 'card':
    case 'swish':
      if (s.linkedBankSourceId) out.add(s.linkedBankSourceId);
      else for (const b of sourceMap.values()) if (b.kind === 'bank' && b.accountNumber === s.accountNumber) out.add(b.id);
      break;
    case 'cash':
    case 'owner':
    case 'supplier_credit':
    case 'customer_credit':
    case 'tax_account':
      break;
    default: {
      const exhaustive: never = s.kind;
      throw new Error(`Unknown payment source kind ${String(exhaustive)}`);
    }
  }
  return out;
}

export function directionOf(kind: TransactionKind): 'out' | 'in' {
  switch (kind) {
    case 'purchase':
    case 'owner_out':
    case 'tax':
      return 'out';
    case 'sale':
    case 'refund':
    case 'owner_in':
      return 'in';
    case 'transfer':
    case 'settle':
    case 'vat_settlement':
    case 'year_end':
    case 'other':
      return 'out';
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown kind ${String(exhaustive)}`);
    }
  }
}

/** The BAS account a proposal's category books on (before special rules). */
export function targetAccountOf(proposal: Pick<Proposal, 'categoryId'>, catalog: readonly Category[]): number | undefined {
  if (!proposal.categoryId) return undefined;
  return findCategory(catalog, proposal.categoryId)?.accountNumber;
}

export interface DuplicateSources {
  verifikationer: readonly Verifikation[];
  items: readonly InboxItem[];
  /**
   * Bookings made without a document that have since been given one through
   * a booked receipt item (`meta.missingDocument` itself never changes). A
   * second look-alike is their duplicate, not their document.
   */
  documentedIds?: ReadonlySet<Uuid> | undefined;
}

/** Verifikationer that a booked receipt item with a file points at. */
export function documentedIdsOf(items: readonly Pick<InboxItem, 'kind' | 'status' | 'attachmentId' | 'verifikationId'>[]): Set<Uuid> {
  const out = new Set<Uuid>();
  for (const it of items) if (it.kind === 'receipt' && it.status === 'booked' && it.attachmentId && it.verifikationId) out.add(it.verifikationId);
  return out;
}

export type DuplicateHit =
  | { kind: 'verifikation'; verifikation: Verifikation; missingDocument: boolean }
  | { kind: 'item'; item: InboxItem };

/**
 * Same merchant + same amount + date ±2 days against booked verifikationer
 * (their `meta`) and other live inbox items. A booked purchase that was
 * booked *without* a document is reported as `missingDocument` so the
 * caller links the receipt instead of flagging a duplicate.
 *
 * Two look-alike receipts that each have their own bank row are two
 * purchases (two coffees, one after the other): `matchedBankRowId` is the
 * probe's row, and an item paired with a different row is not its duplicate.
 * A booking already backed by a different bank row than the probe's is not
 * either.
 */
export function findDuplicate(
  probe: { itemId: Uuid; merchantKey: string; amountOre: number; date: IsoDate; matchedBankRowId?: Uuid | undefined },
  src: DuplicateSources,
): DuplicateHit | undefined {
  if (!probe.merchantKey) return undefined;
  const corrected = new Set(src.verifikationer.filter((v) => v.correctsId).map((v) => v.correctsId));
  for (const v of src.verifikationer) {
    if (!v.meta || v.correctsId || corrected.has(v.id)) continue;
    const key = v.meta.merchantKey ?? merchantKeyOf(v.meta.counterparty);
    const gross = grossOf(v);
    if (!looksLikeDuplicate(probe, { merchantKey: key, amountOre: gross, date: v.date })) continue;
    const bookedRows = v.meta.bankRowIds ?? [];
    if (probe.matchedBankRowId && bookedRows.length && !bookedRows.includes(probe.matchedBankRowId)) continue;
    const documented = src.documentedIds?.has(v.id) === true || v.attachmentIds.length > 0;
    return { kind: 'verifikation', verifikation: v, missingDocument: v.meta.missingDocument && !documented };
  }
  for (const it of src.items) {
    if (it.id === probe.itemId || it.kind !== 'receipt') continue;
    if (it.status === 'skipped' || it.status === 'private' || it.status === 'booked') continue;
    const amount = it.proposal?.amountOre ?? it.reading?.totalOre ?? null;
    const date = it.proposal?.date ?? it.reading?.date ?? null;
    const name = it.proposal?.counterparty || it.reading?.merchant || '';
    if (amount === null || date === null) continue;
    if (!looksLikeDuplicate(probe, { merchantKey: merchantKeyOf(name), amountOre: amount, date })) continue;
    if (probe.matchedBankRowId && it.matchedBankRowIds.length && !it.matchedBankRowIds.includes(probe.matchedBankRowId)) continue;
    return { kind: 'item', item: it };
  }
  return undefined;
}

/** Whether a proposal field is one the user has decided. */
export function isDecided(item: Pick<InboxItem, 'decided'>, field: DecidedField): boolean {
  return item.decided?.includes(field) === true;
}

/** `decided` with `fields` added, without duplicates. */
export function withDecided(item: Pick<InboxItem, 'decided'>, fields: readonly DecidedField[]): DecidedField[] {
  const out = [...(item.decided ?? [])];
  for (const f of fields) if (!out.includes(f)) out.push(f);
  return out;
}

/**
 * Lay the user's decisions over a proposal a reconciliation has just rebuilt
 * from the evidence. The rebuilt proposal is the evidence's reading; every
 * field in `decided` takes the user's value instead. A question about a
 * decided field has been answered and is dropped; a decided kind that books
 * on no category sheds the category and VAT the reader suggested; a decided
 * category or kind makes the proposal the user's own (`reason` null) unless it
 * was a bank match, which is provenance the edit does not undo.
 */
export function overlayDecided(rebuilt: Proposal, decided: readonly DecidedField[], current: Proposal | undefined): Proposal {
  if (!decided.length || !current) return rebuilt;
  const out: InboxProposal = { ...(rebuilt as InboxProposal) };
  const cur = current as InboxProposal;
  for (const field of decided) {
    switch (field) {
      case 'kind':
        out.kind = cur.kind;
        break;
      case 'categoryId':
        out.categoryId = cur.categoryId;
        break;
      case 'paymentSourceId':
        out.paymentSourceId = cur.paymentSourceId;
        break;
      case 'vat':
        out.vat = cur.vat;
        break;
      case 'amountOre':
        out.amountOre = cur.amountOre;
        break;
      case 'date':
        out.date = cur.date;
        break;
      case 'counterparty':
        out.counterparty = cur.counterparty;
        break;
      case 'taxSubkind':
        if (cur.taxSubkind) out.taxSubkind = cur.taxSubkind;
        else delete out.taxSubkind;
        break;
      case 'options':
        if (cur.options) out.options = cur.options;
        else delete out.options;
        break;
      case 'allocations':
        if (cur.allocations) out.allocations = cur.allocations;
        else delete out.allocations;
        break;
      case 'match':
        break;
      default: {
        const exhaustive: never = field;
        throw new Error(`Unknown decided field ${String(exhaustive)}`);
      }
    }
  }
  if (decided.includes('kind') && !kindBooksOnCategory(out.kind)) {
    out.categoryId = null;
    out.vat = null;
  }
  if (out.question && questionDecided(out.question.field, decided)) delete out.question;
  if ((decided.includes('categoryId') || decided.includes('kind')) && out.reason !== 'match') out.reason = null;
  return out;
}

function questionDecided(field: ProposalQuestion['field'], decided: readonly DecidedField[]): boolean {
  switch (field) {
    case 'category':
      return decided.includes('categoryId');
    case 'vat':
      return decided.includes('vat');
    case 'payment':
      return decided.includes('paymentSourceId');
    case 'kind':
      return decided.includes('kind');
    case 'match':
      return decided.includes('match') || decided.includes('allocations');
    case 'persons':
      return decided.includes('options');
    default: {
      const exhaustive: never = field;
      throw new Error(`Unknown question field ${String(exhaustive)}`);
    }
  }
}

export function kindBooksOnCategory(kind: TransactionKind): boolean {
  switch (kind) {
    case 'purchase':
    case 'sale':
    case 'refund':
      return true;
    case 'transfer':
    case 'owner_in':
    case 'owner_out':
    case 'tax':
    case 'settle':
    case 'vat_settlement':
    case 'year_end':
    case 'other':
      return false;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown kind ${String(exhaustive)}`);
    }
  }
}

/** "6 mars · 625,00 kr · K*CLAS OHLSON" — one bank row as an answer option. */
export function bankRowOptionLabel(row: Pick<BankRow, 'bookingDate' | 'transactionDate' | 'amountOre' | 'text'>, relativeTo: IsoDate): string {
  return `${formatSwedishDate(row.transactionDate ?? row.bookingDate, { relativeTo })} · ${formatSek(Math.abs(row.amountOre))} kr · ${row.text.trim() || 'banktransaktion'}`;
}

/** The receipt's "which payment?" question, with the rows it could belong to and a way out. */
export function rowMatchQuestion(candidates: readonly RowCandidate[], relativeTo: IsoDate): ProposalQuestion {
  return {
    field: 'match',
    text: MATCH_QUESTION_TEXT,
    options: [...candidates.map((c) => ({ label: bankRowOptionLabel(c.row, relativeTo), value: c.row.id })), { label: MATCH_NONE_LABEL, value: MATCH_NONE_VALUE }],
  };
}

/** Gross amount of a verifikation: the total debit side. */
export function grossOf(v: Pick<Verifikation, 'rows'>): number {
  return v.rows.reduce((s, r) => s + r.debit, 0);
}

/** Assemble a `TriageInput` from the company context (optional keys only when defined). */
export function triageInput(
  item: InboxItem,
  proposal: Proposal,
  ctx: CompanyContext,
  extra: { reading?: ReceiptReading | undefined; duplicateOf?: Uuid | undefined; categoryConflict?: { remembered: string; read: string } | undefined },
): TriageInput {
  const input: TriageInput = { item, proposal, settings: ctx.settings, openFiscalYears: ctx.openYears, activeAccounts: ctx.activeAccounts };
  if (extra.reading) input.reading = extra.reading;
  if (extra.duplicateOf) input.duplicateOf = extra.duplicateOf;
  if (extra.categoryConflict) input.categoryConflict = extra.categoryConflict;
  if (ctx.lockedThrough !== undefined) input.lockedThrough = ctx.lockedThrough;
  const target = targetAccountOf(proposal, ctx.catalog);
  if (target !== undefined) input.targetAccount = target;
  return input;
}

// ---- Receipt merge (pure over loaded data) ---------------------------------------

export interface ReceiptMergeSources {
  rule: MerchantRule | undefined;
  /**
   * The bank rows this receipt may be paired with. The reconciliation
   * allocates rows across the whole collection before calling this, so two
   * receipts are never offered the same row: the list holds the row assigned
   * to this receipt, or the rows it competes for when none was clear.
   */
  openBankRows: readonly BankRow[];
  duplicates: DuplicateSources;
  /**
   * The user's decisions on the current proposal. `matchedBankRowIds` are the
   * rows a confirmed match holds; they are used as they are, never re-ranked.
   */
  decided?: { fields: readonly DecidedField[]; proposal: Proposal; matchedBankRowIds: readonly Uuid[] } | undefined;
  /** For a credit note: the purchases and invoices of the company it could reduce. */
  refundTargets?: readonly RefundablePurchase[] | undefined;
  /** Rows the user said did not pay this receipt. */
  rejectedRowIds?: ReadonlySet<Uuid> | undefined;
  /**
   * False when the caller has already ruled out an automatic pairing (the
   * clear winner belongs to a row the user has answered on): the rows are
   * then offered as a question, never taken. Default true.
   */
  autoMatch?: boolean | undefined;
  /** Today, for the wording of dates in the match question. */
  today?: IsoDate | undefined;
}

/**
 * Steps 2–4 for one read receipt: memory, bank match, duplicate check, the
 * no-VAT-breakdown rule and triage. Pure; the caller persists the result.
 * Runs again on every reconciliation from the stored extraction, so nothing
 * here may depend on the order receipts and bank rows arrived in.
 */
export function mergeReceipt(item: InboxItem, read: ReceiptReadResult, ctx: CompanyContext, src: ReceiptMergeSources): ProcessedItem {
  const reading = read.reading;
  const merchant = reading.merchant ?? '';
  const decided = src.decided?.fields ?? [];
  const has = (f: DecidedField) => decided.includes(f);
  let proposal: Proposal = {
    kind: read.proposal.kind,
    categoryId: read.proposal.categoryId,
    paymentSourceId: paymentSourceForHint(reading.paymentHint, ctx.paymentSources, ctx.settings),
    vat: read.proposal.vat,
    amountOre: reading.totalOre,
    date: reading.date,
    counterparty: merchant,
    reason: 'suggestion',
    confidence: read.proposal.confidence,
    ...(read.proposal.question ? { question: read.proposal.question } : {}),
  };
  const reasons: string[] = [];

  // 2. Memory.
  let categoryConflict: { remembered: string; read: string } | undefined;
  if (src.rule) {
    const mixed = isAmbiguousCategory(src.rule);
    // Asked before the rule overwrites it: the category the AI read off *this*
    // receipt is the only evidence that the remembered one does not fit. A
    // mixed merchant asserts no category, so there is nothing to disagree with.
    if (!mixed && categoryDisagreesWith(proposal, src.rule)) {
      const readCategory = proposal.categoryId ? findCategory(ctx.catalog, proposal.categoryId) : undefined;
      const remembered = findCategory(ctx.catalog, src.rule.categoryId);
      if (readCategory) {
        categoryConflict = { remembered: remembered?.label ?? src.rule.categoryId, read: readCategory.label };
      }
    }
    const readKind = proposal.kind;
    proposal = applyMerchantRule(proposal, src.rule);
    // Memory remembers how the merchant's purchases are booked; a credit note
    // from the same merchant is still a credit note.
    if (readKind === 'refund') proposal = { ...proposal, kind: 'refund' };
    if (mixed) {
      // Only worth saying when memory actually contributed something.
      if (src.rule.paymentSourceId) reasons.push(mixedMemoryReasonText(src.rule));
    } else {
      const category = findCategory(ctx.catalog, src.rule.categoryId);
      reasons.push(memoryReasonText(src.rule, category?.label ?? src.rule.categoryId));
    }
  }
  // The user's amount, date, kind and counterparty decide what is matched on.
  if (src.decided) {
    const cur = src.decided.proposal;
    if (has('amountOre')) proposal.amountOre = cur.amountOre;
    if (has('date')) proposal.date = cur.date;
    if (has('kind')) proposal.kind = cur.kind;
    if (has('counterparty')) proposal.counterparty = cur.counterparty;
    if (has('paymentSourceId')) proposal.paymentSourceId = cur.paymentSourceId;
  }
  // A user-decided category clears the memory conflict: the user has spoken.
  if (has('categoryId')) categoryConflict = undefined;
  const foreign = (reading.currency || 'SEK').toUpperCase() !== 'SEK';

  // 3. Bank match.
  const matchedBankRowIds: Uuid[] = [];
  let matchQuestion: ProposalQuestion | undefined;
  const rowById = new Map(src.openBankRows.map((r) => [r.id, r]));
  if (src.decided && has('match')) {
    // Confirmed by the user: whatever the scores say now.
    for (const id of src.decided.matchedBankRowIds) if (!src.rejectedRowIds?.has(id)) matchedBankRowIds.push(id);
    const first = matchedBankRowIds[0] ? rowById.get(matchedBankRowIds[0]) : undefined;
    if (first) {
      proposal = { ...proposal, paymentSourceId: first.paymentSourceId, reason: 'match' };
      if (foreign && !has('amountOre')) proposal.amountOre = Math.abs(first.amountOre);
      if (proposal.date) reasons.push(TRIAGE_REASON.match(first.transactionDate ?? first.bookingDate, proposal.date));
    }
  } else if (proposal.amountOre !== null && proposal.date !== null && proposal.amountOre > 0) {
    const ranked = rankBankRowsForReceipt(
      {
        amountOre: proposal.amountOre,
        date: proposal.date,
        merchant: proposal.counterparty,
        currency: reading.currency || 'SEK',
        direction: directionOf(proposal.kind),
        allowedSourceIds: bankSourcesFor(proposal.paymentSourceId, ctx.sourceMap),
      },
      src.openBankRows.filter((r) => !src.rejectedRowIds?.has(r.id)),
    );
    const { auto, ask } = src.autoMatch === false ? { auto: null, ask: ranked.slice(0, 3) } : decideRows(ranked);
    if (auto) {
      const receiptDate = proposal.date;
      matchedBankRowIds.push(auto.row.id);
      proposal = { ...proposal, paymentSourceId: auto.row.paymentSourceId, reason: 'match' };
      if (foreign && !has('amountOre')) proposal.amountOre = Math.abs(auto.row.amountOre);
      reasons.push(TRIAGE_REASON.match(auto.row.transactionDate ?? auto.row.bookingDate, receiptDate));
    } else if (ask.length) {
      // Several rows could have paid it: the receipt asks which, instead of
      // silently taking none and leaving the rows to ask the same thing.
      matchQuestion = rowMatchQuestion(ask, src.today ?? proposal.date);
    }
  }
  /*
   * A total in another currency is not the amount in kronor. Without a bank
   * row to take it from, the proposal has no amount: the card asks for what
   * was drawn from the account instead of presenting "55,00 EUR" as 55 kr.
   * The reading keeps the printed total for the question's wording.
   */
  if (foreign && matchedBankRowIds.length === 0 && !has('amountOre')) proposal.amountOre = null;

  // 3b. A credit note reduces a purchase or an invoice; which one is evidence,
  // not a guess. One clear target is proposed, several are asked about, none
  // needs the user.
  let refundTargetMissing = false;
  if (proposal.kind === 'refund' && !has('allocations')) {
    const targets = src.refundTargets ?? [];
    const rejected = src.rejectedRowIds;
    const ranked =
      proposal.amountOre !== null && proposal.date !== null
        ? rankCreditNoteTargets({ amountOre: proposal.amountOre, date: proposal.date, merchant: proposal.counterparty }, targets).filter((c) => !rejected?.has(c.purchase.verifikationId))
        : [];
    const [top, second] = ranked;
    if (top && proposal.amountOre !== null && (!second || second.score <= top.score - AUTO_LINK_MIN_GAP)) {
      proposal = { ...proposal, allocations: [{ verifikationId: top.purchase.verifikationId, amountOre: proposal.amountOre }], reason: 'match' };
      reasons.push(`${CREDIT_NOTE_REASON(labelOfId(top.purchase.verifikationId, src.duplicates.verifikationer), top.purchase.counterparty)} · ${top.reason}`);
    } else if (top && proposal.amountOre !== null) {
      const relativeTo = src.today ?? proposal.date ?? top.purchase.date;
      proposal = {
        ...proposal,
        allocations: [{ verifikationId: top.purchase.verifikationId, amountOre: proposal.amountOre }],
        question: {
          field: 'match',
          text: 'Vilket köp gäller kreditnotan?',
          options: [
            ...ranked.slice(0, 3).map((c) => ({
              label: `${labelOfId(c.purchase.verifikationId, src.duplicates.verifikationer)} · ${formatSwedishDate(c.purchase.date, { relativeTo })} · ${formatSek(c.purchase.remainingOre)} kr`,
              value: c.purchase.verifikationId,
            })),
            { label: MATCH_NONE_LABEL, value: MATCH_NONE_VALUE },
          ],
        },
      };
    } else {
      refundTargetMissing = true;
    }
  }

  // Duplicate / late document for a "Bokför utan kvitto" booking. Only a
  // verifikation id goes into `duplicateOfVerifikationId`; a duplicate of
  // another inbox item is flagged with its own reason instead.
  let duplicateOf: Uuid | undefined;
  let duplicateOfItemId: Uuid | undefined;
  let linkTo: Verifikation | undefined;
  if (proposal.amountOre !== null && proposal.date !== null && proposal.kind !== 'refund') {
    const hit = findDuplicate(
      { itemId: item.id, merchantKey: merchantKeyOf(proposal.counterparty), amountOre: proposal.amountOre, date: proposal.date, matchedBankRowId: matchedBankRowIds[0] },
      src.duplicates,
    );
    if (hit?.kind === 'verifikation' && hit.missingDocument) linkTo = hit.verifikation;
    else if (hit?.kind === 'verifikation') duplicateOf = hit.verifikation.id;
    else if (hit?.kind === 'item') duplicateOfItemId = hit.item.id;
  }
  const duplicateItem = duplicateOfItemId !== undefined;
  if (linkTo) {
    return {
      status: 'booked',
      tierReasons: [MISSING_DOCUMENT_LINKED(linkTo)],
      flags: read.flags,
      reading,
      proposal,
      matchedBankRowIds,
      verifikationId: linkTo.id,
    };
  }

  // No VAT breakdown never defaults to 25 %.
  const flags = [...read.flags];
  if (duplicateItem && !flags.includes('duplicate')) flags.push('duplicate');
  const noBreakdown = reading.vatLines.length === 0 && ctx.settings.vatRegistered && proposal.kind === 'purchase' && proposal.reason !== 'memory' && !has('vat');
  if (noBreakdown) {
    if (!flags.includes('no_vat_breakdown')) flags.push('no_vat_breakdown');
    proposal = { ...proposal, vat: null, question: NO_VAT_BREAKDOWN_QUESTION };
  }
  // At most one question: the reader's own or the VAT one first, the payment one when nothing else is open.
  if (matchQuestion && !proposal.question) proposal = { ...proposal, question: matchQuestion };

  // The user's decisions win over everything rebuilt above.
  proposal = overlayDecided(proposal, decided, src.decided?.proposal);

  // 4. Triage.
  const probe: InboxItem = { ...item, flags, matchedBankRowIds, ...(duplicateOf ? { duplicateOfVerifikationId: duplicateOf } : {}) };
  const result = triage(triageInput(probe, proposal, ctx, { reading, duplicateOf, categoryConflict }));
  let tier = result.tier;
  const tierReasons = [...reasons, ...(duplicateItem ? [TRIAGE_REASON.duplicateItem] : []), ...result.reasons.filter((r) => !(duplicateItem && r === TRIAGE_REASON.duplicate))];
  if (matchQuestion && proposal.question === matchQuestion) tierReasons.push(AMBIGUOUS_ROWS_REASON);
  if (refundTargetMissing) {
    tier = 'needs_you';
    tierReasons.unshift(CREDIT_NOTE_TARGET_MISSING);
  }
  if (tier === 'ready' && !tierReasons.length) tierReasons.push(TRIAGE_REASON.aiConfident);
  return {
    status: tier,
    tier,
    tierReasons: [...new Set(tierReasons)],
    flags: result.flags,
    reading,
    proposal,
    matchedBankRowIds,
    duplicateOfVerifikationId: duplicateOf,
    duplicateOfItemId,
  };
}

/** "A12" for a verifikation id, or "köpet" when it is not among the given rows. */
export function labelOfId(id: Uuid, verifikationer: readonly Verifikation[]): string {
  const v = verifikationer.find((x) => x.id === id);
  return v ? `${v.series}${v.number}` : 'köpet';
}

// ---- Bank row merge (pure over loaded data) -----------------------------------------

export interface BankRowMergeSources {
  row: BankRow;
  /** What the bookkeeping still expects, with what earlier rows of this pass already claimed subtracted. */
  openItems: readonly OpenItem[];
  siblingRows: readonly BankRow[];
  rule: MerchantRule | undefined;
  classification: BankRowClassificationResult | undefined;
  verifikationById: ReadonlyMap<Uuid, Verifikation>;
  /** Booked purchases money coming back could refund. */
  refundables?: readonly RefundablePurchase[] | undefined;
  /** Verifikationer the user said this row does not belong to. */
  rejected?: ReadonlySet<Uuid> | undefined;
}

export type BankRowMerge =
  | { kind: 'link'; verifikationId: Uuid; processed: ProcessedItem }
  | { kind: 'proposal'; processed: ProcessedItem };

function kindQuestion(text: string, options: { label: string; value: string }[]): ProposalQuestion {
  return { field: 'kind', text, options };
}

/** A section 9 question card as the proposal's one question (answers go through `answerQuestion`). */
export function cardQuestion(card: QuestionCard): ProposalQuestion {
  return kindQuestion(card.text, card.options);
}

function baseProposal(row: BankRow, kind: TransactionKind, counterparty: string): Proposal {
  return {
    kind,
    categoryId: null,
    paymentSourceId: row.paymentSourceId,
    vat: null,
    amountOre: Math.abs(row.amountOre),
    date: row.transactionDate ?? row.bookingDate,
    counterparty,
    reason: null,
    confidence: 0,
  };
}

/** The allocation a bank row makes to one open item: the row, or what the item still owes, whichever is less. */
export function allocationFor(row: Pick<BankRow, 'amountOre'>, item: Pick<OpenItem, 'verifikationId' | 'amountOre'>): ProposalAllocation {
  return { verifikationId: item.verifikationId, amountOre: Math.min(Math.abs(row.amountOre), Math.abs(item.amountOre)) };
}

/**
 * Section 9 priorities (1)–(7) for one bank row, extended for the pile: an
 * instalment or a payment for several invoices settles with explicit
 * allocations, money back from a merchant asks whether it refunds the
 * purchase, and a stored AI classification is reused. Pure; the caller
 * persists and links.
 */
export function mergeBankRow(item: InboxItem, ctx: CompanyContext, src: BankRowMergeSources): BankRowMerge {
  const { row } = src;
  const rowDate = row.transactionDate ?? row.bookingDate;
  const rejected = src.rejected;
  const finishWith = (proposal: Proposal, extraReasons: string[] = [], matched: Uuid[] = []): ProcessedItem => {
    const result = triage(triageInput({ ...item, matchedBankRowIds: matched }, proposal, ctx, {}));
    return { status: result.tier, tier: result.tier, tierReasons: [...extraReasons, ...result.reasons], flags: result.flags, proposal, matchedBankRowIds: matched };
  };
  const labelOf = (id: Uuid) => {
    const v = src.verifikationById.get(id);
    return v ? `${v.series}${v.number}` : '';
  };

  // (1) Already linked.
  if (row.status !== 'open') {
    const vId = row.linkedVerifikationIds[0];
    const v = vId ? src.verifikationById.get(vId) : undefined;
    return {
      kind: 'proposal',
      processed: { status: 'booked', tierReasons: [v ? reconciledText(v) : 'Redan avstämd.'], flags: [], matchedBankRowIds: [], ...(vId ? { verifikationId: vId } : {}) },
    };
  }

  // (2)/(3) Expected settlement or open invoice, whole or in part.
  const openItems = rejected?.size ? src.openItems.filter((o) => !rejected.has(o.verifikationId)) : src.openItems;
  const candidates: Candidate[] = rankCandidates(row, openItems);
  // Once the user has said no to a candidate they are choosing: the rest are
  // offered, never taken.
  const decision = rejected?.size ? { auto: null, ask: candidates.slice(0, 3) } : decide(candidates);
  if (decision.auto) {
    const target = decision.auto.item;
    const label = labelOf(target.verifikationId);
    const allocation = allocationFor(row, target);
    switch (target.kind) {
      case 'settlement': {
        const v = src.verifikationById.get(target.verifikationId);
        return {
          kind: 'link',
          verifikationId: target.verifikationId,
          processed: { status: 'booked', tierReasons: [v ? reconciledText(v) : 'Avstämd.', decision.auto.reason], flags: [], matchedBankRowIds: [], verifikationId: target.verifikationId },
        };
      }
      case 'reimbursement': {
        // The payback of "Egna pengar": propose the kvittning (D 2893 / K bank), never a silent link.
        const proposal: Proposal = { ...baseProposal(row, 'settle', target.counterparty), reason: 'match', confidence: 1, allocations: [allocation] };
        return { kind: 'proposal', processed: finishWith(proposal, [`${REIMBURSEMENT_REASON(label || 'utlägget', target.counterparty)} · ${decision.auto.reason}`]) };
      }
      case 'customer_invoice':
      case 'supplier_invoice': {
        const proposal: Proposal = { ...baseProposal(row, 'settle', target.counterparty), reason: 'match', confidence: 1, allocations: [allocation] };
        const remaining = Math.abs(target.amountOre) - allocation.amountOre;
        const reason = decision.auto.partial ? PARTIAL_SETTLEMENT_REASON(label || 'fakturan', target.counterparty, remaining) : INVOICE_SETTLEMENT_REASON(label || 'fakturan', target.counterparty);
        return { kind: 'proposal', processed: finishWith(proposal, [`${reason} · ${decision.auto.reason}`]) };
      }
      default: {
        const exhaustive: never = target.kind;
        throw new Error(`Unknown open item kind ${String(exhaustive)}`);
      }
    }
  }
  // One payment for several invoices: the sum is only a lead; the user confirms.
  const combos = rankCombinations(row, openItems);
  const combo = combos[0];
  if (combo) {
    const counterparty = combo.items[0]?.counterparty ?? '';
    const labels = combo.items.map((i) => labelOf(i.verifikationId) || 'fakturan');
    const proposal: Proposal = {
      ...baseProposal(row, 'settle', counterparty),
      reason: 'match',
      confidence: combo.score / 100,
      allocations: combo.items.map((i) => allocationFor(row, i)),
      question: cardQuestion(combinationQuestion(labels, counterparty)),
    };
    return { kind: 'proposal', processed: finishWith(proposal, [`${COMBINED_SETTLEMENT_REASON(labels, counterparty)} · ${combo.reason}`]) };
  }
  if (decision.ask.length) {
    const top = decision.ask[0]!;
    const proposal: Proposal = {
      ...baseProposal(row, row.amountOre < 0 ? 'purchase' : 'sale', top.item.counterparty),
      reason: 'match',
      confidence: top.score / 100,
      // The candidate the card asks about, so "Nej, visa fler" knows what to rule out.
      allocations: [allocationFor(row, top.item)],
      question: cardQuestion(matchQuestion(top, rowDate)),
    };
    return { kind: 'proposal', processed: finishWith(proposal, decision.ask.map((c) => c.reason)) };
  }

  // (4) Own transfer. A pair the user rejected is not offered again.
  const pair = findTransferPair(row, rejected?.size ? src.siblingRows.filter((r) => !rejected.has(r.id)) : src.siblingRows);
  if (pair || looksLikeOwnTransferText(row.text)) {
    const name = (id: Uuid) => ctx.sourceMap.get(id)?.name ?? 'annat konto';
    const from = row.amountOre < 0 ? row : pair;
    const to = row.amountOre < 0 ? pair : row;
    const q = transferQuestion(from ? name(from.paymentSourceId) : 'annat konto', to ? name(to.paymentSourceId) : 'annat konto');
    const proposal: Proposal = {
      ...baseProposal(row, 'transfer', ''),
      paymentSourceId: from?.paymentSourceId ?? row.paymentSourceId,
      reason: pair ? 'rule' : 'suggestion',
      confidence: pair ? 0.9 : 0.7,
      question: cardQuestion(q),
    };
    return { kind: 'proposal', processed: finishWith(proposal, [], pair ? [pair.id] : []) };
  }

  // (5) Skatteverket.
  if (isSkatteverketText(row.text)) {
    const q = taxQuestion(ctx.company.entityType);
    const proposal: Proposal = { ...baseProposal(row, 'tax', 'Skatteverket'), reason: 'rule', confidence: 0.9, question: cardQuestion(q) };
    return { kind: 'proposal', processed: finishWith(proposal) };
  }

  // (5b) Money back from a merchant the company bought from.
  if (row.amountOre > 0 && src.refundables?.length) {
    const refunds = rankRefundCandidates(row, src.refundables).filter((c) => !rejected?.has(c.purchase.verifikationId));
    const top = refunds[0];
    if (top) {
      const label = labelOf(top.purchase.verifikationId);
      // The refund mirrors the purchase's rows; its category is the purchase's, for the sentence.
      const originalMeta = src.verifikationById.get(top.purchase.verifikationId)?.meta;
      const proposal: Proposal = {
        ...baseProposal(row, 'refund', top.purchase.counterparty),
        categoryId: originalMeta?.categoryId ?? null,
        vat: originalMeta?.vat ?? null,
        reason: 'match',
        confidence: top.score / 100,
        allocations: [{ verifikationId: top.purchase.verifikationId, amountOre: row.amountOre }],
        question: cardQuestion(refundQuestion(top.purchase.counterparty, label, top.purchase.date, rowDate)),
      };
      return { kind: 'proposal', processed: finishWith(proposal, [`${REFUND_REASON(label || 'köpet', top.purchase.counterparty)} · ${top.reason}`]) };
    }
  }

  // (6) Merchant rule.
  if (src.rule) {
    const seed = baseProposal(row, src.rule.kind, src.rule.displayName);
    const proposal = applyMerchantRule({ ...seed, confidence: 1 }, src.rule);
    proposal.paymentSourceId = row.paymentSourceId;
    const category = findCategory(ctx.catalog, src.rule.categoryId);
    return { kind: 'proposal', processed: finishWith(proposal, [memoryReasonText(src.rule, category?.label ?? src.rule.categoryId)]) };
  }

  // (7) AI classification.
  const c = src.classification;
  if (c) {
    const proposal: Proposal = {
      ...baseProposal(row, c.kind, c.counterparty || row.text.trim()),
      categoryId: c.categoryId,
      vat: c.vat,
      reason: 'suggestion',
      confidence: c.confidence,
      // A purchase the AI thinks should have a receipt gets the "Har du ett kvitto?" card.
      ...(c.needsReceipt && c.kind === 'purchase' && row.amountOre < 0 ? { question: cardQuestion(receiptQuestion()) } : {}),
    };
    return { kind: 'proposal', processed: finishWith(proposal) };
  }

  // Else ask.
  if (row.amountOre > 0) {
    const proposal: Proposal = { ...baseProposal(row, 'sale', row.text.trim()), reason: null, confidence: 0, question: cardQuestion(moneyInQuestion(row.text)) };
    return { kind: 'proposal', processed: finishWith(proposal) };
  }
  const proposal: Proposal = { ...baseProposal(row, 'purchase', row.text.trim()), question: cardQuestion(receiptQuestion()) };
  return { kind: 'proposal', processed: finishWith(proposal) };
}

// ---- Rate limits -------------------------------------------------------------------

export class PipelineWait extends Error {
  constructor(
    public readonly scope: 'item' | 'run',
    message: string,
  ) {
    super(message);
    this.name = 'PipelineWait';
  }
}

export class PipelineAbort extends Error {
  constructor() {
    super('Avbrutet.');
    this.name = 'PipelineAbort';
  }
}

function fallbackToUiError(err: unknown): PipelineUiError {
  const message = err instanceof Error ? err.message : String(err);
  if (/\b429\b|rate.?limit|RESOURCE_EXHAUSTED/i.test(message)) return { kind: 'rate-limit-minute', message: WAITING_FOR_QUOTA, retryAfterSec: 60 };
  return { kind: 'other', message: GENERIC_AI_ERROR, technical: message };
}

export function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new PipelineAbort());
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(new PipelineAbort());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Run an AI call with 429 backoff: per-minute limits and overload wait
 * `retryAfterSec` (capped) and retry a few times; a per-day limit stops the
 * whole run. Anything else is rethrown as an Error with a Swedish message.
 */
export async function withAiRetry<T>(fn: () => Promise<T>, deps: InboxDeps, signal: AbortSignal | undefined): Promise<T> {
  const toUi = deps.toUiError ?? fallbackToUiError;
  const sleep = deps.sleep ?? defaultSleep;
  const max = deps.maxRateLimitRetries ?? DEFAULT_MAX_RATE_LIMIT_RETRIES;
  for (let attempt = 0; ; attempt++) {
    if (signal?.aborted) throw new PipelineAbort();
    try {
      return await fn();
    } catch (err) {
      if (err instanceof PipelineAbort || signal?.aborted) throw new PipelineAbort();
      const ui = toUi(err);
      switch (ui.kind) {
        case 'rate-limit-day':
          throw new PipelineWait('run', WAITING_FOR_DAY_QUOTA);
        case 'rate-limit-minute':
        case 'overloaded': {
          if (attempt >= max) throw new PipelineWait('item', WAITING_FOR_QUOTA);
          const sec = Math.min(MAX_BACKOFF_SEC, Math.max(1, ui.retryAfterSec ?? 30));
          await sleep(sec * 1000, signal);
          continue;
        }
        case 'auth':
        case 'billing':
        case 'model':
        case 'bad-json':
        case 'network':
        case 'other':
          /*
           * Carry the UiError on the thrown error. `technicalOfError` reads
           * `ui.technical` off it and stores it as `errorTechnical`, which is
           * the only route the provider's own message takes to the user's
           * "Tekniska detaljer". A bare `new Error(ui.message)` dropped it one
           * line before the code that saves it, so every AI failure looked
           * like a Swedish sentence with no way to find out what happened.
           * `ui` rather than the AiError itself: the domain may not import
           * from `src/ai`.
           */
          throw Object.assign(new Error(ui.message), { ui });
        default: {
          const exhaustive: never = ui.kind;
          throw new Error(String(exhaustive));
        }
      }
    }
  }
}

// ---- Runner --------------------------------------------------------------------------

/** An inbox item patch; `undefined` values are dropped before the write. */
export type ItemPatch = { [K in keyof InboxItem]?: InboxItem[K] | undefined };

export async function patchItem(db: BookkeepingDB, id: Uuid, patch: ItemPatch): Promise<void> {
  const changes = { ...compact(patch), updatedAt: new Date().toISOString() };
  const clears = (Object.keys(patch) as (keyof InboxItem)[]).filter((k) => patch[k] === undefined);
  if (!clears.length) {
    await db.inboxItems.update(id, changes);
    return;
  }
  await db.inboxItems
    .where('id')
    .equals(id)
    .modify((item) => {
      Object.assign(item, changes);
      for (const k of clears) delete item[k];
    });
}

/** Link a bank row to an existing verifikation without booking anything ("Avstämd mot A12"). */
export async function linkRow(db: BookkeepingDB, row: BankRow, verifikationId: Uuid, inboxItemId?: Uuid): Promise<void> {
  const linked = row.linkedVerifikationIds.includes(verifikationId) ? row.linkedVerifikationIds : [...row.linkedVerifikationIds, verifikationId];
  await db.bankRows.update(row.id, { status: 'linked', linkedVerifikationIds: linked, ...(inboxItemId ? { inboxItemId } : {}) });
}

/**
 * Give every open bank row in `rowIds` an inbox item, once. Idempotent: rows
 * that already have one, are not open, or belong to another company are left
 * alone. `batchId` records which handover the rows arrived in.
 */
export async function ensureBankRowItems(db: BookkeepingDB, companyId: Uuid, rowIds: readonly Uuid[], batchId?: string | undefined): Promise<InboxItem[]> {
  if (!rowIds.length) return [];
  return db.transaction('rw', db.inboxItems, db.bankRows, async () => {
    const rows = await db.bankRows.where('id').anyOf([...rowIds]).toArray();
    const existing = await db.inboxItems.where('bankRowId').anyOf([...rowIds]).toArray();
    const taken = new Set(existing.map((i) => i.bankRowId));
    const out: InboxItem[] = [];
    for (const row of rows.sort((a, b) => a.bookingDate.localeCompare(b.bookingDate) || a.id.localeCompare(b.id))) {
      if (row.companyId !== companyId || row.status !== 'open' || taken.has(row.id)) continue;
      const stamp = new Date().toISOString();
      const item: InboxItem = {
        id: crypto.randomUUID(),
        companyId,
        kind: 'bank_row',
        status: 'new',
        tierReasons: [],
        flags: [],
        source: 'bank',
        bankRowId: row.id,
        matchedBankRowIds: [],
        ...(batchId ? { batchId } : {}),
        createdAt: stamp,
        updatedAt: stamp,
      };
      await db.inboxItems.add(item);
      await db.bankRows.update(row.id, { inboxItemId: item.id });
      taken.add(row.id);
      out.push(item);
    }
    return out;
  });
}

/**
 * Items a run left in `processing` and never finished (the tab closed, the
 * browser crashed): back to `new`, so the next run picks them up. Anything
 * younger than `STALE_PROCESSING_MS` may belong to a run in another tab.
 */
export async function reclaimStaleProcessing(db: BookkeepingDB, companyId: Uuid, now: number = Date.now()): Promise<number> {
  const stuck = await db.inboxItems.where('[companyId+status]').equals([companyId, 'processing']).toArray();
  const stale = stuck.filter((i) => now - Date.parse(i.updatedAt) >= STALE_PROCESSING_MS);
  for (const item of stale) await patchItem(db, item.id, { status: 'new' });
  return stale.length;
}

/** A receipt merged with a bank row hides that row's own inbox item (working state only). */
export async function retireBankRowItems(db: BookkeepingDB, bankRowIds: Uuid[]): Promise<void> {
  if (!bankRowIds.length) return;
  const items = await db.inboxItems.where('bankRowId').anyOf(bankRowIds).toArray();
  const ids = items.filter((i) => i.status !== 'booked').map((i) => i.id);
  if (ids.length) await db.inboxItems.bulkDelete(ids);
}

export const FILING_FAILED = 'Kvittot är bokfört men kunde inte sparas i arkivkopian. Arkivkontrollen försöker igen.';

/**
 * Put a booked receipt in Kvitton/<år>/ of the archive copy, whichever way it
 * has to get there. Throws when it could not; callers turn that into
 * `FILING_FAILED` on the item and the archive check retries later.
 *
 * A receipt that reached the cloud Inkorg is moved (`fileReceipt`). One that
 * never did — added before a storage was connected, or booked before the
 * upload queue had finished with it — is uploaded straight from the local
 * blob instead of being left out of the archive until someone runs the
 * check by hand. The item is re-read first: the queue writes the id back
 * asynchronously and may have done so since the caller loaded it. Any queue
 * row still pending for the item is dropped, so the copy does not turn up in
 * Inkorg afterwards next to the filed one.
 */
export async function fileBookedReceipt(
  item: Pick<InboxItem, 'id' | 'companyId' | 'attachmentId'>,
  v: Verifikation,
  counterparty: string,
  amountOre: Ore,
  deps: InboxDeps,
  db: BookkeepingDB,
): Promise<void> {
  const fresh = await db.inboxItems.get(item.id);
  const driveFileId = fresh?.driveFileId;
  if (driveFileId) {
    if (deps.fileReceipt) await deps.fileReceipt({ companyId: item.companyId, driveFileId, verifikation: v, counterparty, amountOre }, db);
    return;
  }
  if (!deps.uploadReceipt || !item.attachmentId) return;
  const attachment = await db.attachments.get(item.attachmentId);
  if (!attachment?.blob) return;
  const queued = await db.uploadQueue.filter((q) => q.appProperties.inboxItemId === item.id && q.status !== 'done').primaryKeys();
  if (queued.length) await db.uploadQueue.bulkDelete(queued);
  const filed = await deps.uploadReceipt({ companyId: item.companyId, verifikation: v, counterparty, amountOre, blob: attachment.blob, mediaType: attachment.mediaType }, db);
  await db.transaction('rw', db.attachments, db.inboxItems, async () => {
    await db.attachments.update(attachment.id, { driveFileId: filed.id });
    await db.inboxItems.update(item.id, { driveFileId: filed.id, updatedAt: new Date().toISOString() });
  });
}

/** A receipt that turned out to be the document of an earlier booking: file it in the archive copy. */
export async function fileLateDocument(item: InboxItem, verifikationId: Uuid, deps: InboxDeps, db: BookkeepingDB): Promise<void> {
  const v = await db.verifikationer.get(verifikationId);
  if (!v) return;
  try {
    await fileBookedReceipt(item, v, v.meta?.counterparty ?? '', grossOf(v), deps, db);
  } catch {
    await patchItem(db, item.id, { error: FILING_FAILED });
  }
}

/** "Matchad mot banktransaktion 6 mars" for the review card. */
export function matchReasonText(row: Pick<BankRow, 'bookingDate' | 'transactionDate'>, relativeTo: IsoDate): string {
  return `Matchad mot banktransaktion ${formatSwedishDate(row.transactionDate ?? row.bookingDate, { relativeTo })}`;
}
